import type { CTASectionProps } from 'mdxui'
import { resolveAction } from '../../lib/resolve-action'

/**
 * Variant mapping from mdxui CTA variants to vendor block components.
 * Both split-media variants render CtaSplitMedia; the gradient backdrop is toggled
 * via the `gradient` prop rather than a separate component.
 */
export const CTA_VARIANT_MAP = {
  'split-media': 'CtaSplitMedia',
  'split-media-gradient': 'CtaSplitMedia',
} as const

export type CtaVariant = keyof typeof CTA_VARIANT_MAP

/**
 * Vendor CtaSplitMedia props (split with media; gradient optional)
 */
export interface CtaSplitMediaMapperProps {
  label?: string
  heading?: string
  description?: string
  buttons?: {
    primary?: {
      text: string
      url: string
    }
    secondary?: {
      text: string
      url: string
    }
  }
  image?: {
    src: string
    alt: string
  }
  mediaType?: 'image' | 'code'
  codeWindow?: {
    code: string
    language: string
  }
  codeTheme?: 'dark' | 'light' | 'auto'
  /** Radial gradient + glow background; false for a flat split-media look */
  gradient?: boolean
  className?: string
}

/** Extended props for CTA variants (omit variant since it's handled by the adapter) */
export type CtaInputProps = Omit<CTASectionProps, 'variant'> & {
  /** CTA layout variant */
  variant?: CtaVariant
  /** Small label text above heading (for split-media-gradient variant) */
  label?: string
  imageSrc?: string
  imageAlt?: string
  mediaType?: 'image' | 'code'
  codeWindow?: {
    code: string
    language: string
  }
  codeTheme?: 'dark' | 'light' | 'auto'
}

/**
 * Map mdxui CTASectionProps to vendor CtaSplitMedia props. The gradient backdrop is on
 * for the 'split-media-gradient' variant and off for plain 'split-media'.
 */
export function mapToCtaSplitMediaProps(props: CtaInputProps): CtaSplitMediaMapperProps {
  const primaryUrl = resolveAction(props.actions?.primary) || '#'
  const secondaryUrl = props.secondaryCallToAction
    ? resolveAction(props.actions?.secondary) || '#'
    : undefined

  return {
    label: props.label,
    heading: props.title,
    description: props.description,
    buttons: {
      primary: props.callToAction
        ? {
            text: props.callToAction,
            url: primaryUrl,
          }
        : undefined,
      secondary: props.secondaryCallToAction
        ? {
            text: props.secondaryCallToAction,
            url: secondaryUrl!,
          }
        : undefined,
    },
    image: props.imageSrc
      ? {
          src: props.imageSrc,
          alt: props.imageAlt || 'Image',
        }
      : undefined,
    mediaType: props.mediaType,
    codeWindow: props.codeWindow,
    codeTheme: props.codeTheme,
    gradient: props.variant === 'split-media-gradient',
  }
}

/**
 * Map mdxui CTASectionProps to the appropriate vendor props based on variant.
 * Both variants resolve to CtaSplitMedia (gradient on/off handled in the mapped props).
 */
export function mapCtaSectionProps(props: CtaInputProps): {
  variant: (typeof CTA_VARIANT_MAP)[CtaVariant]
  props: CtaSplitMediaMapperProps
} {
  return {
    variant: 'CtaSplitMedia',
    props: mapToCtaSplitMediaProps(props),
  }
}
